import { useCallback, useEffect } from "react";
import { Buyer, CreateBuyer, UpdateBuyer } from "../types/models/Buyer.d";
import { UpdateEntityBuyer } from "../types/models/Buyer";
import { useGetBuyerListQuery, useInsertBuyerMutation, useUpdateBuyerMutation, useDeleteBuyerMutation, } from "../apis/Buyer.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useBuyer(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertBuyerMutation, { 
        isLoading: insertBuyerLoading, 
        isSuccess: insertBuyerIsSuccess,
        isError: insertBuyerIsError,
        error: insertBuyerError,
    }] = useInsertBuyerMutation()
    const [updateBuyerMutation, { 
        isLoading: updateBuyerLoading,
        isSuccess: updateBuyerIsSuccess,
        isError: updateBuyerIsError,
        error: updateBuyerError,
    }] = useUpdateBuyerMutation()
    const [deleteBuyerMutation, { 
        isLoading: deleteBuyerLoading,
        isSuccess: deleteBuyerIsSuccess,
        isError: deleteBuyerIsError,
        error: deleteBuyerError,
    }] = useDeleteBuyerMutation()

    const loading =
        insertBuyerLoading ||
        updateBuyerLoading ||
        deleteBuyerLoading ||
        false

    const insertBuyer = useCallback(async (buyer: CreateBuyer, redirectPath: string|undefined) => {
        try {
            const data = await insertBuyerMutation(buyer).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertBuyerMutation, navigate])
    
    useEffect(() => {
        if (insertBuyerIsSuccess) {
            openSuccessAlert('Buyer created')
        } else if (insertBuyerIsError) {
            console.log("add error", insertBuyerError)
            openFailAlert(t`Buyer add error: ` + insertBuyerError?.data?.message)
        }
    }, [
        insertBuyerIsSuccess,
        insertBuyerIsError,
        insertBuyerError
    ]);

    const updateBuyer = useCallback(async (buyer: UpdateEntityBuyer, redirectPath: string|undefined) => {
        try {
            const data = await updateBuyerMutation(buyer).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateBuyerMutation, navigate])
    
    useEffect(() => {
        if (updateBuyerIsSuccess) {
            openSuccessAlert(t`Buyer updated`)
        } else if (updateBuyerIsError) {
            console.log("update error", updateBuyerError)
            openFailAlert(t`Buyer update error: ` + updateBuyerError?.data?.message)
        }
    }, [
        updateBuyerIsSuccess,
        updateBuyerIsError,
        updateBuyerError
    ]);

    const deleteBuyer = useCallback((buyer: Buyer, redirectPath: string|undefined) => {
        openDialog(async () => {
            buyer = {
                ...buyer,
            }
            try {
                const { data } = await deleteBuyerMutation(buyer);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteBuyerMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteBuyerIsSuccess) {
            openSuccessAlert(t`Buyer deleted`)
        } else if (deleteBuyerIsError) {
            console.log("delete error", deleteBuyerError)
            openFailAlert(t`Buyer delete error: ` + deleteBuyerError?.data?.message)
        }
    }, [
        deleteBuyerIsSuccess,
        deleteBuyerIsError,
        deleteBuyerError
    ]);

    return {
        loading,
        insertBuyer,
        updateBuyer,
        deleteBuyer
    }
}
